import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Search, TriangleAlert, Loader2, ScanSearch, RotateCcw, Archive, Clock3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AreasTab } from "@/components/admin/AreaWipe";

const TABS = [
  ["areas", "Wipe by area"],
  ["detector", "Test-data detector"],
  ["accounts", "Accounts"],
  ["buyers", "Ping-tree buyers"],
  ["leads", "Leads"],
  ["inbox", "Inbox"],
  ["recycle", "Recycle bin"],
];

export default function Cleanup() {
  const [tab, setTab] = useState("areas");
  return (
    <div className="space-y-6">
      <SectionTitle
        title="Data cleanup"
        subtitle="Delete test accounts, dud buyers, fake leads and old messages. Every deletion is snapshotted to the recycle bin for 30 days and logged in the audit trail."
      />
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`cleanup-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? "border-[#0055FF] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "areas" && <AreasTab />}
      {tab === "detector" && <DetectorTab />}
      {tab === "accounts" && <AccountsTab />}
      {tab === "buyers" && <BuyersTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "inbox" && <InboxTab />}
      {tab === "recycle" && <RecycleTab />}
    </div>
  );
}

/* ── Test-data detector ───────────────────────────────── */
function DetectorTab() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [delLeads, setDelLeads] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/cleanup/detect").then((r) => {
    setData(r.data);
    const next = {};
    (r.data.users || []).forEach((u) => { next[u.id] = true; });
    setSel(next);
  });
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-6 text-sm text-slate-500">Scanning for test data…</div>;

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const nothingFlagged = data.users.length === 0 && data.leads_total === 0;
  const willDelete = selIds.length + (delLeads ? data.leads_total : 0);

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup/detect/delete", { user_ids: selIds, delete_flagged_leads: delLeads && data.leads_total > 0 });
      toast.success(`Cleaned up: ${r.data.users_deleted} accounts, ${r.data.related_deleted} related records, ${r.data.leads_deleted} leads — all recoverable for 30 days`);
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="detector-tab">
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 flex items-start gap-3">
        <ScanSearch className="size-5 text-[#0055FF] mt-0.5 shrink-0" />
        <div className="text-[12.5px] text-slate-600 leading-relaxed">
          Scans accounts and leads for test patterns — <span className="font-mono text-[11px]">test, demo, fake, dummy, example, iter…</span> in emails, names, companies and sources.
          Review the flags below, untick anything real, then clean up in one click. Everything lands in the recycle bin for 30 days.
        </div>
      </div>

      {nothingFlagged && (
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-10 text-center text-sm text-slate-500" data-testid="detector-clean">
            ✨ Nothing suspicious found — your data looks clean.
          </CardContent>
        </Card>
      )}

      {data.users.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Flagged accounts ({data.users.length})</h3>
            <button className="text-[11px] text-slate-500 hover:text-slate-700" onClick={() => {
              const allOn = data.users.every((u) => sel[u.id]);
              const next = {};
              if (!allOn) data.users.forEach((u) => { next[u.id] = true; });
              setSel(next);
            }} data-testid="detector-toggle-all">Toggle all</button>
          </div>
          <ListCard empty={false}>
            {data.users.map((u) => (
              <li key={u.id} className="p-3.5 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`detector-user-${u.id}`}>
                <input type="checkbox" className="accent-[#0055FF]" checked={!!sel[u.id]} onChange={(e) => setSel({ ...sel, [u.id]: e.target.checked })} data-testid={`detector-user-check-${u.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">{u.name || "—"}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${ROLE_HUE[u.role] || "bg-slate-100 text-slate-600"}`}>{u.role}</span>
                    <span className="text-[11px] font-mono text-slate-500">{u.email}</span>
                  </div>
                  <div className="mt-1 flex gap-1.5">
                    {(u.reasons || []).map((r) => (
                      <span key={r} className="text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">flagged: {r}</span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ListCard>
        </div>
      )}

      {data.leads_total > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Flagged leads ({data.leads_total.toLocaleString("en-GB")})</h3>
          <Card className="rounded-xl shadow-soft border-slate-200">
            <CardContent className="p-4">
              <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="accent-[#0055FF]" checked={delLeads} onChange={(e) => setDelLeads(e.target.checked)} data-testid="detector-leads-check" />
                Delete all {data.leads_total.toLocaleString("en-GB")} flagged leads in this cleanup
              </label>
              <div className="mt-3 space-y-1 max-h-44 overflow-auto">
                {data.leads_sample.slice(0, 8).map((l) => (
                  <div key={l.id} className="text-[11px] font-mono text-slate-500 truncate">
                    {l.email} · {l.sector} · {l.source || "—"} <span className="text-amber-600">({(l.reasons || []).join(", ")})</span>
                  </div>
                ))}
                {data.leads_total > 8 && <div className="text-[11px] text-slate-400">…and {(data.leads_total - 8).toLocaleString("en-GB")} more</div>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!nothingFlagged && (
        <div className="flex justify-end">
          <Button
            disabled={willDelete === 0}
            onClick={() => setConfirm(true)}
            data-testid="detector-cleanup-btn"
            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
          >
            <Trash2 className="size-4" /> Clean up {willDelete.toLocaleString("en-GB")} flagged record{willDelete === 1 ? "" : "s"}
          </Button>
        </div>
      )}

      <ConfirmDelete
        open={confirm}
        onClose={() => setConfirm(false)}
        title={`Clean up ${willDelete.toLocaleString("en-GB")} flagged records?`}
        description={`${selIds.length} account${selIds.length === 1 ? "" : "s"} (with all attached data)${delLeads && data.leads_total > 0 ? ` and ${data.leads_total.toLocaleString("en-GB")} flagged leads` : ""} will be moved to the recycle bin — recoverable for 30 days.`}
        onConfirm={doDelete}
        busy={busy}
      />
    </div>
  );
}

/* ── Recycle bin ──────────────────────────────────────── */
const KIND_HUE = {
  user: "bg-blue-50 text-blue-700", leads: "bg-emerald-50 text-emerald-700",
  buyer: "bg-violet-50 text-violet-700", inbox: "bg-amber-50 text-amber-700",
  sweep: "bg-[#0055FF]/10 text-[#0044CC]",
};

function RecycleTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = () => api.get("/admin/cleanup/recycle").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const restore = async (entry) => {
    setRestoring(entry.id);
    try {
      const r = await api.post(`/admin/cleanup/recycle/${entry.id}/restore`);
      toast.success(`Restored ${r.data.restored} record${r.data.restored === 1 ? "" : "s"} from "${entry.label}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(null); }
  };

  const purge = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/cleanup/recycle/${purging.id}`);
      toast.success("Purged permanently");
      setPurging(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  const daysLeft = (e) => (e.days_left != null ? e.days_left : Math.max(0, Math.ceil((new Date(e.expires_at) - Date.now()) / 86400000)));
  const records = rows.reduce((n, e) => n + (e.total || 0), 0);
  const soonest = rows.length ? Math.min(...rows.map(daysLeft)) : null;
  const expiringWeek = rows.filter((e) => daysLeft(e) <= 7);

  return (
    <div className="space-y-4" data-testid="recycle-tab">
      {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}

      {!loading && rows.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3" data-testid="recycle-summary">
          <Tile label="Waiting in the bin" value={rows.length.toLocaleString("en-GB")}
                note={`${records.toLocaleString("en-GB")} record${records === 1 ? "" : "s"} in total`} testid="recycle-tile-batches" />
          <Tile label="Next clear-out" value={`${soonest}d`}
                note={soonest === 0 ? "clears on the next visit to this page" : "then that snapshot is gone for good"}
                tone={soonest <= 7 ? "soon" : "calm"} testid="recycle-tile-next" />
          <Tile label="Going in the next 7 days" value={expiringWeek.length.toLocaleString("en-GB")}
                note={expiringWeek.length ? "restore now if you still need any of it" : "nothing about to disappear"}
                tone={expiringWeek.length ? "soon" : "calm"} testid="recycle-tile-week" />
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-10 text-center" data-testid="recycle-empty">
            <Archive className="size-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">The recycle bin is empty. Deleted records appear here for 30 days before being purged.</p>
          </CardContent>
        </Card>
      )}
      {!loading && rows.length > 0 && (
        <ListCard empty={false}>
          {rows.map((e) => {
            const d = daysLeft(e);
            const soon = d <= 7;
            return (
              <li key={e.id} className="p-4 flex items-start gap-3 hover:bg-slate-50/60" data-testid={`recycle-entry-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${KIND_HUE[e.kind] || "bg-slate-100 text-slate-600"}`}>{(e.kind || "").replace("area:", "")}</span>
                    <span className="text-sm font-medium text-slate-900">{e.label}</span>
                    <span className="text-[11px] text-slate-400">· {e.total.toLocaleString("en-GB")} record{e.total === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {Object.entries(e.counts || {}).slice(0, 6).map(([coll, n]) => (
                      <span key={coll} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono text-slate-500">
                        {coll.replace(/_/g, " ")} <b className="text-slate-700">{n.toLocaleString("en-GB")}</b>
                      </span>
                    ))}
                    {Object.keys(e.counts || {}).length > 6 && (
                      <span className="px-1 py-0.5 text-[10px] text-slate-400">+{Object.keys(e.counts).length - 6} more</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[10.5px] font-mono text-slate-400">
                      Deleted {new Date(e.deleted_at).toLocaleString("en-GB")} by {e.actor}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      soon ? "border-[#0055FF]/30 bg-[#0055FF]/[0.07] text-[#0044CC]" : "border-slate-200 bg-slate-50 text-slate-500"}`}
                          data-testid={`recycle-expiry-${e.id}`}>
                      <Clock3 className="size-2.5" />
                      {d === 0 ? "clears today" : `clears itself in ${d} day${d === 1 ? "" : "s"}`}
                    </span>
                  </div>
                  <div className="mt-2 h-1 w-full max-w-xs overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${soon ? "bg-[#0055FF]" : "bg-slate-300"}`}
                         style={{ width: `${Math.max(3, Math.min(100, (d / 30) * 100))}%` }} />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full h-8 px-3" disabled={restoring === e.id} onClick={() => restore(e)} data-testid={`recycle-restore-${e.id}`}>
                  {restoring === e.id ? <Loader2 className="size-3.5 animate-spin" /> : <><RotateCcw className="size-3.5" /> Restore</>}
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full h-8 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setPurging(e)} data-testid={`recycle-purge-${e.id}`}>
                  <Trash2 className="size-3.5" /> Purge
                </Button>
              </li>
            );
          })}
        </ListCard>
      )}
      <ConfirmDelete
        open={!!purging}
        onClose={() => setPurging(null)}
        title={`Permanently purge "${purging?.label}"?`}
        description="This removes the snapshot for good — after this there is genuinely no undo."
        onConfirm={purge}
        busy={busy}
      />
    </div>
  );
}

function Tile({ label, value, note, tone = "calm", testid }) {
  const t = tone === "soon"
    ? "border-[#0055FF]/25 bg-[#0055FF]/[0.04]"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-4 ${t}`} data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-[22px] font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="mt-0.5 text-[11.5px] text-slate-500">{note}</div>
    </div>
  );
}

/* ── shared confirm dialog: type DELETE to proceed ────── */
function ConfirmDelete({ open, onClose, title, description, preview, onConfirm, busy }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="cleanup-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <TriangleAlert className="size-5" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {preview && preview.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3.5 text-[12.5px] text-rose-800 space-y-1" data-testid="cleanup-cascade-preview">
            <div className="font-semibold text-[11px] uppercase tracking-wider">Will also permanently remove</div>
            {preview.map((p, i) => (
              <div key={i} className="flex justify-between font-mono">
                <span>{p.collection}</span><span>{p.count.toLocaleString("en-GB")}</span>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Type <b className="font-mono text-slate-900">DELETE</b> to confirm. Deleted records sit in the recycle bin for 30 days.</p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" data-testid="cleanup-confirm-input" className="font-mono" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="cleanup-confirm-cancel">Cancel</Button>
          <Button
            disabled={typed !== "DELETE" || busy}
            onClick={onConfirm}
            data-testid="cleanup-confirm-delete"
            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><Trash2 className="size-4" /> Delete permanently</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative max-w-sm flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9 h-9" data-testid="cleanup-search" />
    </div>
  );
}

const ROLE_HUE = { client: "bg-blue-50 text-blue-700", publisher: "bg-emerald-50 text-emerald-700", affiliate: "bg-violet-50 text-violet-700" };

/* ── Accounts ─────────────────────────────────────────── */
function AccountsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/cleanup/users?q=${encodeURIComponent(q)}&role=${role}`).then((r) => setRows(r.data || []));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, role]);

  const openConfirm = async (u) => {
    setTarget(u);
    setPreview([]);
    try {
      const r = await api.get(`/admin/cleanup/users/${u.id}/preview`);
      setPreview(r.data.related || []);
    } catch (_) { /* noop */ }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/cleanup/users/${target.id}`);
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${target.email} and ${total - 1} related records`);
      setTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBox value={q} onChange={setQ} placeholder="Search name, email or company…" />
        {["all", "client", "publisher", "affiliate"].map((r) => (
          <button key={r} onClick={() => setRole(r)} data-testid={`cleanup-role-${r}`}
            className={`rounded-full text-xs font-medium px-3 py-1.5 border capitalize transition-all ${role === r ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}>
            {r}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-auto">{rows.length} account{rows.length === 1 ? "" : "s"}</span>
      </div>
      <ListCard empty={rows.length === 0}>
        {rows.map((u) => (
          <li key={u.id} className="p-4 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`cleanup-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">{u.name || "—"}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${ROLE_HUE[u.role] || "bg-slate-100 text-slate-600"}`}>{u.role}</span>
                <span className="text-[11px] font-mono text-slate-500">{u.email}</span>
                {u.company && <span className="text-[11px] text-slate-400">· {u.company}</span>}
              </div>
              <div className="text-[10.5px] font-mono text-slate-400 mt-0.5">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"}</div>
            </div>
            <Button variant="ghost" size="sm" className="rounded-full h-8 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => openConfirm(u)} data-testid={`cleanup-delete-user-${u.id}`}>
              <Trash2 className="size-3.5" /> Delete
            </Button>
          </li>
        ))}
      </ListCard>
      <ConfirmDelete
        open={!!target}
        onClose={() => setTarget(null)}
        title={`Delete ${target?.name || target?.email}?`}
        description={`This removes the ${target?.role} account "${target?.email}" and everything attached to it.`}
        preview={preview}
        onConfirm={doDelete}
        busy={busy}
      />
    </div>
  );
}

/* ── Ping-tree buyers ─────────────────────────────────── */
function BuyersTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/cleanup/buyers?q=${encodeURIComponent(q)}`).then((r) => setRows(r.data || []));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/cleanup/buyers/${target.id}`);
      toast.success(`Deleted buyer "${target.name}"`);
      setTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search buyer name…" />
        <span className="text-xs text-slate-400 ml-auto">{rows.length} buyer{rows.length === 1 ? "" : "s"}</span>
      </div>
      <ListCard empty={rows.length === 0}>
        {rows.map((b) => (
          <li key={b.id} className="p-4 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`cleanup-buyer-${b.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">{b.name}</span>
                <Badge variant="outline" className={`rounded-full text-[10px] ${b.live ? "text-emerald-600 border-emerald-200" : "text-slate-500"}`}>{b.live ? "live" : "off"}</Badge>
                {typeof b.acceptance === "number" && <span className="text-[11px] text-slate-500">{Math.round(b.acceptance * 100)}% acceptance</span>}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">{(b.sectors || []).join(" · ") || "no sectors"}</div>
            </div>
            <Button variant="ghost" size="sm" className="rounded-full h-8 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setTarget(b)} data-testid={`cleanup-delete-buyer-${b.id}`}>
              <Trash2 className="size-3.5" /> Delete
            </Button>
          </li>
        ))}
      </ListCard>
      <ConfirmDelete
        open={!!target}
        onClose={() => setTarget(null)}
        title={`Delete buyer "${target?.name}"?`}
        description="Removes this buyer from the routing pool. Existing routed leads keep their history."
        onConfirm={doDelete}
        busy={busy}
      />
    </div>
  );
}

/* ── Leads ────────────────────────────────────────────── */
function LeadsTab() {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState(null); // "selected" | "all"
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/cleanup/leads?q=${encodeURIComponent(q)}`).then((r) => { setData(r.data); setSel({}); });
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const toggleAll = () => {
    const allOn = data.rows.length > 0 && data.rows.every((r) => sel[r.id]);
    const next = {};
    if (!allOn) data.rows.forEach((r) => { next[r.id] = true; });
    setSel(next);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const payload = confirm === "all" ? { query: q, delete_all_matching: true } : { ids: selIds };
      const r = await api.post("/admin/cleanup/leads/delete", payload);
      toast.success(`Deleted ${r.data.deleted.toLocaleString("en-GB")} lead${r.data.deleted === 1 ? "" : "s"}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBox value={q} onChange={setQ} placeholder='Search email, name, phone or source (e.g. "test")…' />
        <span className="text-xs text-slate-400">{data.total.toLocaleString("en-GB")} matching · showing {data.rows.length}</span>
        <div className="ml-auto flex gap-2">
          {selIds.length > 0 && (
            <Button size="sm" variant="outline" className="rounded-full h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setConfirm("selected")} data-testid="cleanup-delete-selected-leads">
              <Trash2 className="size-3.5" /> Delete selected ({selIds.length})
            </Button>
          )}
          {q.trim() && data.total > 0 && (
            <Button size="sm" variant="outline" className="rounded-full h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setConfirm("all")} data-testid="cleanup-delete-all-leads">
              <Trash2 className="size-3.5" /> Delete ALL {data.total.toLocaleString("en-GB")} matching
            </Button>
          )}
        </div>
      </div>
      <ListCard empty={data.rows.length === 0}>
        <li className="px-4 py-2 bg-slate-50/80 flex items-center gap-3 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
          <input type="checkbox" className="accent-[#0055FF]" onChange={toggleAll} checked={data.rows.length > 0 && data.rows.every((r) => sel[r.id])} data-testid="cleanup-leads-select-all" />
          <span>Select all shown</span>
        </li>
        {data.rows.map((l) => (
          <li key={l.id} className="p-3.5 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`cleanup-lead-${l.id}`}>
            <input type="checkbox" className="accent-[#0055FF]" checked={!!sel[l.id]} onChange={(e) => setSel({ ...sel, [l.id]: e.target.checked })} data-testid={`cleanup-lead-check-${l.id}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-900">{[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}</span>
                <span className="text-[11px] font-mono text-slate-500">{l.email}</span>
                {l.phone && <span className="text-[11px] font-mono text-slate-400">{l.phone}</span>}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">{l.sector} · {l.source || "unknown source"} · {l.status} · {l.created_at ? new Date(l.created_at).toLocaleDateString("en-GB") : ""}</div>
            </div>
          </li>
        ))}
      </ListCard>
      <ConfirmDelete
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === "all" ? `Delete ALL ${data.total.toLocaleString("en-GB")} matching leads?` : `Delete ${selIds.length} selected lead${selIds.length === 1 ? "" : "s"}?`}
        description={confirm === "all" ? `Every lead matching "${q}" will be permanently removed — including ones not shown on this page.` : "The selected leads will be permanently removed."}
        onConfirm={doDelete}
        busy={busy}
      />
    </div>
  );
}

/* ── Inbox ────────────────────────────────────────────── */
const TYPE_LABEL = { enquiry: "Enquiry", contact: "Contact", demo: "Demo request" };
const TYPE_HUE = { enquiry: "bg-blue-50 text-blue-700", contact: "bg-slate-100 text-slate-600", demo: "bg-amber-50 text-amber-700" };

function InboxTab() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/cleanup/inbox").then((r) => { setRows(r.data || []); setSel({}); });
  useEffect(() => { load(); }, []);

  const key = (r) => `${r.type}:${r.id}`;
  const selected = rows.filter((r) => sel[key(r)]);

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup/inbox/delete", { items: selected.map((s) => ({ type: s.type, id: s.id })) });
      toast.success(`Deleted ${r.data.deleted} message${r.data.deleted === 1 ? "" : "s"}`);
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">{rows.length} messages across enquiries, contact forms and demo requests</span>
        {selected.length > 0 && (
          <Button size="sm" variant="outline" className="ml-auto rounded-full h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setConfirm(true)} data-testid="cleanup-delete-inbox">
            <Trash2 className="size-3.5" /> Delete selected ({selected.length})
          </Button>
        )}
      </div>
      <ListCard empty={rows.length === 0}>
        {rows.map((r) => (
          <li key={key(r)} className="p-3.5 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`cleanup-inbox-${r.id}`}>
            <input type="checkbox" className="accent-[#0055FF]" checked={!!sel[key(r)]} onChange={(e) => setSel({ ...sel, [key(r)]: e.target.checked })} data-testid={`cleanup-inbox-check-${r.id}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${TYPE_HUE[r.type]}`}>{TYPE_LABEL[r.type]}</span>
                <span className="text-sm font-medium text-slate-900">{r.name}</span>
                <span className="text-[11px] font-mono text-slate-500">{r.email}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">{r.summary}</div>
            </div>
            <span className="text-[10.5px] font-mono text-slate-400 shrink-0">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : ""}</span>
          </li>
        ))}
      </ListCard>
      <ConfirmDelete
        open={confirm}
        onClose={() => setConfirm(false)}
        title={`Delete ${selected.length} message${selected.length === 1 ? "" : "s"}?`}
        description="The selected enquiries / messages will be permanently removed."
        onConfirm={doDelete}
        busy={busy}
      />
    </div>
  );
}

function ListCard({ children, empty }) {
  return (
    <Card className="rounded-xl shadow-soft border-slate-200">
      <CardContent className="p-0">
        {empty ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="cleanup-empty">Nothing found.</div>
        ) : (
          <ul className="divide-y divide-slate-100">{children}</ul>
        )}
      </CardContent>
    </Card>
  );
}
